import { useCallback, useEffect, useState } from "react";
import { dayjs } from "@/utils/date";

/** Identifiers for the built-in date presets, plus "custom" for a manual range. */
export type DatePresetId = "24h" | "7d" | "30d" | "currentMonth" | "previousMonth" | "custom";

/** A resolved date range in ISO-8601 form. */
export interface DateRange {
  start: string;
  end: string;
}

const DATE_PRESETS: Array<{
  id: Exclude<DatePresetId, "custom">;
  label: string;
  description: string;
  range: () => DateRange;
}> = [
  {
    id: "24h",
    label: "Últimas 24h",
    description: "1 dia",
    range: () => {
      const end = dayjs();
      const start = end.clone().subtract(1, "day");
      return { start: start.toISOString(), end: end.toISOString() };
    },
  },
  {
    id: "7d",
    label: "Últimos 7 dias",
    description: "7 dias",
    range: () => {
      const end = dayjs();
      const start = end.clone().subtract(7, "day");
      return { start: start.toISOString(), end: end.toISOString() };
    },
  },
  {
    id: "30d",
    label: "Últimos 30 dias",
    description: "30 dias",
    range: () => {
      const end = dayjs();
      const start = end.clone().subtract(30, "day");
      return { start: start.toISOString(), end: end.toISOString() };
    },
  },
  {
    id: "currentMonth",
    label: "Mês atual",
    description: "Até hoje",
    range: () => {
      const end = dayjs();
      const start = end.clone().startOf("month");
      return { start: start.toISOString(), end: end.toISOString() };
    },
  },
  {
    id: "previousMonth",
    label: "Mês anterior",
    description: "1º ao último dia",
    range: () => {
      const end = dayjs().startOf("month").subtract(1, "day").endOf("day");
      const start = end.clone().startOf("month");
      return { start: start.toISOString(), end: end.toISOString() };
    },
  },
];

function toInputValue(value: string) {
  const date = dayjs(value);
  return date.isValid() ? date.format("YYYY-MM-DDTHH:mm") : "";
}

/**
 * Normalizes a range: invalid dates fall back to the last 24h, and a swapped
 * range is reversed so start is never after end.
 *
 * @param startIso - Raw start timestamp.
 * @param endIso - Raw end timestamp.
 * @returns The normalized range.
 */
function normalizeRange(startIso: string, endIso: string): DateRange {
  const start = dayjs(startIso);
  const end = dayjs(endIso);
  if (!start.isValid() || !end.isValid()) {
    const now = dayjs();
    return {
      start: now.clone().subtract(1, "day").toISOString(),
      end: now.toISOString(),
    };
  }
  if (start.isAfter(end)) {
    return { start: end.toISOString(), end: start.toISOString() };
  }
  return { start: start.toISOString(), end: end.toISOString() };
}

/**
 * Props for the shared date-range preset control.
 */
export interface DateRangePresetsProps {
  /** Currently applied start timestamp (ISO), used to seed the custom inputs. */
  startDate: string;
  /** Currently applied end timestamp (ISO), used to seed the custom inputs. */
  endDate: string;
  /** Disables every control (e.g. no property selected yet). */
  disabled?: boolean;
  /** Called with a resolved (normalized) range whenever the user applies one. */
  onApply: (range: DateRange) => void;
}

/**
 * Shared date-range bar: preset chips (24h / 7d / 30d / current month /
 * previous month) plus a "Personalizar" mode with `datetime-local` inputs.
 * The custom inputs re-seed whenever `startDate`/`endDate` change externally
 * (e.g. from a deep-link URL).
 *
 * @param props - See {@link DateRangePresetsProps}.
 */
export function DateRangePresets({ startDate, endDate, disabled = false, onApply }: DateRangePresetsProps) {
  const [selectedPreset, setSelectedPreset] = useState<DatePresetId>("24h");
  const [customOpen, setCustomOpen] = useState(false);
  const [customRange, setCustomRange] = useState(() => ({
    start: toInputValue(startDate),
    end: toInputValue(endDate),
  }));

  useEffect(() => {
    setCustomRange({
      start: toInputValue(startDate),
      end: toInputValue(endDate),
    });
  }, [startDate, endDate]);

  const handlePresetClick = useCallback(
    (presetId: DatePresetId) => {
      if (presetId === "custom") {
        setCustomOpen((open) => !open);
        setSelectedPreset("custom");
        return;
      }
      const preset = DATE_PRESETS.find((item) => item.id === presetId);
      if (!preset) return;
      onApply(preset.range());
      setSelectedPreset(presetId);
      setCustomOpen(false);
    },
    [onApply]
  );

  const handleApplyCustomRange = useCallback(() => {
    if (!customRange.start || !customRange.end) return;
    onApply(normalizeRange(dayjs(customRange.start).toISOString(), dayjs(customRange.end).toISOString()));
    setSelectedPreset("custom");
    setCustomOpen(false);
  }, [customRange.end, customRange.start, onApply]);

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Intervalo de datas</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {DATE_PRESETS.map((preset) => {
          const active = selectedPreset === preset.id;
          return (
            <button
              key={preset.id}
              type="button"
              disabled={disabled}
              onClick={() => handlePresetClick(preset.id)}
              className={`inline-flex items-center rounded-full border px-3 py-1 text-sm transition ${
                active
                  ? "border-transparent bg-blue-600 text-white shadow"
                  : "border-neutral-300 bg-white text-gray-700 hover:bg-neutral-100"
              } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
              title={preset.description}
            >
              {preset.label}
            </button>
          );
        })}
        <button
          type="button"
          disabled={disabled}
          onClick={() => handlePresetClick("custom")}
          className={`inline-flex items-center rounded-full border px-3 py-1 text-sm transition ${
            selectedPreset === "custom"
              ? "border-blue-500 bg-blue-50 text-blue-700"
              : "border-neutral-300 bg-white text-gray-700 hover:bg-neutral-100"
          } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
        >
          Personalizar
        </button>
      </div>

      {customOpen ? (
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
            Início
            <input
              type="datetime-local"
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              value={customRange.start}
              onChange={(event) =>
                setCustomRange((prev) => ({
                  ...prev,
                  start: event.target.value,
                }))
              }
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
            Fim
            <input
              type="datetime-local"
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              value={customRange.end}
              onChange={(event) =>
                setCustomRange((prev) => ({
                  ...prev,
                  end: event.target.value,
                }))
              }
            />
          </label>
          <div className="md:col-span-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleApplyCustomRange}
              className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Aplicar intervalo
            </button>
            <button
              type="button"
              onClick={() => setCustomOpen(false)}
              className="inline-flex items-center rounded-md border border-neutral-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-neutral-100"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
